<template>
  <Transition
    enter-active-class="toast-enter-active"
    enter-from-class="toast-enter-from"
    enter-to-class="toast-enter-to"
    leave-active-class="toast-leave-active"
    leave-from-class="toast-leave-from"
    leave-to-class="toast-leave-to"
  >
    <div
      v-if="isVisible"
      ref="toastElement"
      class="toast"
      :class="toastClasses"
      role="alert"
      :aria-live="type === 'error' ? 'assertive' : 'polite'"
      :aria-label="props['aria-label']"
      @click="handleClick"
      @mouseenter="pauseTimer"
      @mouseleave="resumeTimer"
    >
      <!-- Toast Content -->
      <div class="toast-content">
        <!-- Icon -->
        <div v-if="showIcon" class="toast-icon">
          <BaseIcon size="small" :variant="iconVariant">
            <component :is="iconComponent" />
          </BaseIcon>
        </div>

        <!-- Content -->
        <div class="toast-body">
          <!-- Title -->
          <div v-if="title" class="toast-title">{{ title }}</div>
          
          <!-- Message or Slot -->
          <div class="toast-message">
            <slot>
              {{ message }}
            </slot>
          </div>
        </div>

        <!-- Close Button -->
        <button
          v-if="showCloseButton"
          class="toast-close"
          @click.stop="dismiss"
          aria-label="Close notification"
          type="button"
        >
          <BaseIcon size="small" variant="ghost">
            <XIcon />
          </BaseIcon>
        </button>
      </div>

      <!-- Progress Bar -->
      <div
        v-if="showProgress && !persistent"
        class="toast-progress"
        :class="progressClasses"
      >
        <div
          class="toast-progress-bar"
          :style="progressStyle"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue'
import BaseIcon from '@/components/ui/BaseIcon.vue'
import XIcon from '../icons/XIcon.vue'
import CheckCircleIcon from '../icons/CheckCircleIcon.vue'
import ExclamationTriangleIcon from '../icons/ExclamationTriangleIcon.vue'
import InformationCircleIcon from '../icons/InformationCircleIcon.vue'
import XCircleIcon from '../icons/XCircleIcon.vue'

interface Props {
  id?: string
  type?: 'success' | 'error' | 'warning' | 'info' | 'custom'
  title?: string
  message?: string
  duration?: number
  persistent?: boolean
  showCloseButton?: boolean
  showProgress?: boolean
  showIcon?: boolean
  'aria-label'?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  duration: 2500,
  persistent: false,
  showCloseButton: true,
  showProgress: true,
  showIcon: true
})

const emit = defineEmits<{
  dismiss: [id?: string]
  click: [event: MouseEvent]
}>()

// State
const isVisible = ref(true)
const isPaused = ref(false)
const progress = ref(100)
const toastElement = ref<HTMLElement>()

// Timer
let timer: NodeJS.Timeout | null = null
let progressTimer: NodeJS.Timeout | null = null

// Computed properties
const toastClasses = computed(() => [
  'toast',
  `toast--${props.type}`,
  {
    'toast--persistent': props.persistent,
    'toast--clickable': !props.persistent
  }
])

const iconComponent = computed(() => {
  switch (props.type) {
    case 'success': return CheckCircleIcon
    case 'error': return XCircleIcon
    case 'warning': return ExclamationTriangleIcon
    case 'info': return InformationCircleIcon
    default: return InformationCircleIcon
  }
})

const iconVariant = computed(() => {
  switch (props.type) {
    case 'success': return 'success'
    case 'error': return 'danger'
    case 'warning': return 'warning'
    case 'info': return 'primary'
    default: return 'ghost'
  }
})

const progressClasses = computed(() => [
  'toast-progress',
  `toast-progress--${props.type}`
])

const progressStyle = computed(() => ({
  width: `${progress.value}%`,
  transition: isPaused.value ? 'none' : `width ${props.duration}ms linear`
}))

// Methods
const dismiss = () => {
  isVisible.value = false
  clearTimers()
  emit('dismiss', props.id)
}

const handleClick = (event: MouseEvent) => {
  if (!props.persistent) {
    emit('click', event)
    dismiss()
  }
}

const pauseTimer = () => {
  if (props.persistent) return
  isPaused.value = true
  clearTimers()
}

const resumeTimer = () => {
  if (props.persistent) return
  isPaused.value = false
  startTimers()
}

const startTimers = () => {
  if (props.persistent) return
  
  // Auto-dismiss timer
  timer = setTimeout(() => {
    dismiss()
  }, props.duration)

  // Progress bar timer
  if (props.showProgress) {
    const progressInterval = 50 // Update every 50ms
    const totalSteps = props.duration / progressInterval
    const stepSize = 100 / totalSteps
    
    progressTimer = setInterval(() => {
      if (!isPaused.value) {
        progress.value = Math.max(0, progress.value - stepSize)
      }
    }, progressInterval)
  }
}

const clearTimers = () => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
}

// Lifecycle
onMounted(async () => {
  await nextTick()
  startTimers()
})

onUnmounted(() => {
  clearTimers()
})
</script>

<style scoped>
.toast {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 20rem;
  max-width: 24rem;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  
}

.toast--clickable {
  cursor: pointer;
}

.toast--clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Type-specific styling */
.toast--success {
  background-color: var(--color-toast-success-bg);
  border-left: 4px solid var(--color-toast-success-border);
}

.toast--success .toast-title {
  color: var(--color-toast-success-text);
}

.toast--success .toast-message {
  color: var(--color-toast-success-text);
  opacity: 0.8;
}

.toast--error {
  background-color: var(--color-toast-error-bg);
  border-left: 4px solid var(--color-toast-error-border);
}

.toast--error .toast-title {
  color: var(--color-toast-error-text);
}

.toast--error .toast-message {
  color: var(--color-toast-error-text);
  opacity: 0.8;
}

.toast--warning {
  background-color: var(--color-toast-warning-bg);
  border-left: 4px solid var(--color-toast-warning-border);
}

.toast--warning .toast-title {
  color: var(--color-toast-warning-text);
}

.toast--warning .toast-message {
  color: var(--color-toast-warning-text);
  opacity: 0.8;
}

.toast--info {
  background-color: var(--color-toast-info-bg);
  border-left: 4px solid var(--color-toast-info-border);
}

.toast--info .toast-title {
  color: var(--color-toast-info-text);
}

.toast--info .toast-message {
  color: var(--color-toast-info-text);
  opacity: 0.8;
}

.toast--custom {
  background-color: var(--color-toast-custom-bg);
  border-left: 4px solid var(--color-toast-custom-border);
}

.toast--custom .toast-title {
  color: var(--color-toast-custom-text);
}

.toast--custom .toast-message {
  color: var(--color-toast-custom-text);
  opacity: 0.8;
}

/* Content */
.toast-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
}

.toast-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.toast-body {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-text);
  margin-bottom: 0.25rem;
}

.toast-message {
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-text-muted);
}

.toast-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background-color: transparent;
  border: none;
  border-radius: 0.25rem;
  color: var(--color-text-muted);
  cursor: pointer;
  
  flex-shrink: 0;
}

.toast-close:hover {
  background-color: var(--color-surface-muted);
  color: var(--color-text);
}

.toast-close:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Progress Bar */
.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background-color: var(--color-surface-muted);
  overflow: hidden;
}

.toast-progress-bar {
  height: 100%;
  
}

.toast-progress--success .toast-progress-bar {
  background-color: var(--color-success);
}

.toast-progress--error .toast-progress-bar {
  background-color: var(--color-danger);
}

.toast-progress--warning .toast-progress-bar {
  background-color: var(--color-warning);
}

.toast-progress--info .toast-progress-bar {
  background-color: var(--color-primary);
}

/* Transitions */
.toast-enter-active,
.toast-leave-active {
  
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.toast-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .toast {
    min-width: 16rem;
    max-width: 20rem;
  }
  
  .toast-content {
    padding: 0.75rem;
  }
  
  .toast-title {
    font-size: 0.8125rem;
  }
  
  .toast-message {
    font-size: 0.8125rem;
  }
}
</style>
